// Signs a trainer in on the shared floor iPad by name and PIN.
//
// The server answers with an ACC session that is already pinned to the HeroTrainer role (never Manager or Admin), so what
// this module writes is exactly what a Google sign-in leaves behind: the `acc_session` cookie
// the command client attaches, and the `user` cookie the auth provider reads. The one extra
// fact is `user.kiosk` plus the `bbvr_kiosk` flag, which is what keeps Google re-mint logic,
// the four-hour timer and the manager surfaces away from a session nobody verified by Google.

import Cookies from 'js-cookie';
import { clearAccSession, parseExpiresAt, writeAccSession } from './accSession';
import { dropKioskIdentity, isKioskSession, setKioskFlag } from './kioskFlag';
import { clearAgentSessionToken, clearGoogleAccessToken } from './googleAccessToken';
import { requestKioskSession } from './Kiosk/kioskApi';
import { resetUserCaches } from './userCaches';

export { isKioskSession };

/** Falls back to the server's 12 hours when the answer carries no readable expiry. */
const KIOSK_SESSION_MS = 12 * 60 * 60 * 1000;

export const isKioskUser = (user) => user?.kiosk === true;

/**
 * Exchanges name + PIN for a kiosk session and stores it. Resolves the signed-in user object
 * (what `user` should become). Throws the accClient error on a refusal, with `code` and
 * `details`; nothing is stored then. The PIN is never kept or logged here.
 */
export const startKioskSession = async (name, pin) => {
    const data = await requestKioskSession(name, pin);
    if (typeof data?.session !== 'string' || data.session.length === 0) {
        const error = new Error('Request did not go through.');
        error.code = null;
        error.details = null;
        throw error;
    }
    const expiresAt = data.expiresAt ? parseExpiresAt(data.expiresAt) : Date.now() + KIOSK_SESSION_MS;
    const expires = new Date(expiresAt);
    const user = { name: data.trainer || name, email: '', kiosk: true };
    // A Google token left in memory by whoever used this tab before would outrank the PIN session.
    clearGoogleAccessToken();
    clearAgentSessionToken();
    writeAccSession(data.session, expiresAt, expires);
    Cookies.set('user', JSON.stringify(user), { expires, sameSite: 'lax', secure: window.location.protocol === 'https:' });
    setKioskFlag();
    resetUserCaches();
    return user;
};

/** Signs the device out of the kiosk: both cookies and the flag. */
export const endKioskSession = (notice) => {
    clearAccSession();
    resetUserCaches();
    dropKioskIdentity(notice);
};
